import { CommonModule, formatCurrency } from '@angular/common';
import {
    Component,
    EventEmitter,
    Injector,
    Input,
    InputSignal,
    OnInit,
    Output,
    Signal,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { IqControlTextStateService } from '../state';

import { NgxCurrencyDirective } from 'ngx-currency';
import { toSignal } from '@angular/core/rxjs-interop';
import { IqEventBusBrokerService } from '@dat/iq-event-bus';

@Component({
    selector: 'iq-control-text-currency',
    standalone: true,
    imports: [CommonModule, NgxCurrencyDirective, ReactiveFormsModule],
    templateUrl: './iq-control-text-currency.component.html',
    styleUrl: './iq-control-text-currency.component.scss',
})
export class IqControlTextCurrencyComponent implements OnInit {
    randomName: string =
        'iq-control-text-percent-' + Math.random().toString(36).substring(7);

    @Input()
    fg: FormGroup = new FormGroup({});

    @Output()
    keyupped: EventEmitter<number> = new EventEmitter<number>();

    keyUp($event: any) {
        const currency = this.currencyValue();
        if (this.disabled() || this._isValueOutOfBounds(currency)) return;

        this.keyupped.emit(currency);
    }

    internalFormGroup = this._fb.group({});

    inputControl: FormControl;

    errorMessageSignal: Signal<string> = signal('');
    errorMessage: Signal<string> = computed((): string => {
        return this.errorMessageSignal();
    });

    // Call this in the OnInit Function
    private _setErrorMessageSignal(): void {
        this.errorMessageSignal = this._state.errorMessage(this.inputControl);
    }

    id: InputSignal<string> = input('');
    placeholder: InputSignal<string> = input('');
    value: InputSignal<number> = input(0);
    name: InputSignal<string> = input('');
    disabled: InputSignal<boolean> = input(null);
    minValue: InputSignal<number> = input(null);
    maxValue: InputSignal<number> = input(null);
    required: InputSignal<boolean> = input(false);
    prefix: InputSignal<string> = input('$');
    suffix: InputSignal<string> = input('');

    currencyOptions: Signal<any> = computed(() => {
        return {
            suffix: this.suffix() ?? '',
            prefix: this.prefix() ?? '$',
            min: 0,
            max: 99999,
            align: 'left',
        };
    });

    passedValue: Signal<number> = signal(0);

    currencySignal: Signal<number> = signal(0);
    currencyValue: Signal<number> = computed((): number => {
        return this.currencySignal();
    });

    private _events: IqEventBusBrokerService = inject(IqEventBusBrokerService);

    // Call this in the OnInit Function
    private _setCurrencySignal(): void {
        this.currencySignal = this._state.currency(
            this.name,
            this.value,
            this.internalFormGroup,
            {
                disabled: this.disabled,
                minValue: this.minValue,
                maxValue: this.maxValue,
                required: this.required,
            },
        );
    }

    private _isValueOutOfBounds(currencyValue: number) {
        const hasMinOrMaxValue = !!this.minValue() || !!this.maxValue();
        return (
            hasMinOrMaxValue &&
            (currencyValue < this.minValue() || currencyValue > this.maxValue())
        );
    }

    constructor(
        private readonly _state: IqControlTextStateService,
        private readonly _fb: FormBuilder,
        private readonly _injector: Injector,
    ) {
        effect(() => {
            const currency = this.currencyValue();
            if (this.disabled()) return;

            /**
             * This dispatches an event indicating the tabs have changed without the user interaction
             *
             *
             * To listen to these events, you can use the following format in your Dashboard, Section, or Component:
             *
             * ```json
             * {
             *  "onChange": [
             *    {
             *      "name": "EVENT_NAME",
             *      "payload": {},
             *      "sourceEventRule": {
             *          "payload": [
             *            {
             *                "path": "control.id",
             *                "operator": "eq",
             *                "value": "CONTROL_ID"
             *            },
             *          ]
             *      },
             *      "rules": []
             *    },
             *   ]
             * }
             * ```
             */
            const dispatch = () =>
                this._events.dispatch({
                    name: 'type:change',
                    payload: {
                        id: this.id(),
                        componentName: 'IqControlTextCurrency',
                        ...this._createEmitData(),
                        value: currency,
                    },
                });

            untracked(() => {
                this.fg.get(this.name()).setValue(currency);

                dispatch();
            });
        });

        effect(() => {
            const passedValue = this.passedValue();
            untracked(() => {
                this.inputControl.setValue(passedValue);
            });
        });
    }

    ngOnInit(): void {
        this._setCurrencySignal();
        this.inputControl = this.internalFormGroup.get(
            this.name(),
        ) as FormControl;
        this.fg.addControl(this.name(), this._fb.control(this.value()));
        this.passedValue = toSignal(this.fg.get(this.name()).valueChanges, {
            injector: this._injector,
        });
        this._setErrorMessageSignal();
        this.inputControl.valueChanges.subscribe((value) => {
            const dispatch = () =>
                this._events.dispatch({
                    name: 'type:change',
                    payload: {
                        id: this.id(),
                        componentName: 'IqControlTextCurrency',
                        ...this._createEmitData(),
                        value: this.passedValue(),
                    },
                });
            const customFuel = this.fg.get(this.name());
            if (value > this.maxValue()) {
                customFuel.setErrors({ invalid: true });
            } else {
                customFuel.setErrors(null);
            }

            dispatch();
        });
    }

    private _createEmitData(): { [key: string]: any } {
        const emitData = {
            form: {
                ...this.fg.value,
                valid: this.fg.valid,
                errors: this.fg.errors,
            },
            control: {
                id: this.id(),
                value: this.value(),
                name: this.name(),
                type: 'percent',
                valid: this.fg.get(this.name()).valid,
            },
        };
        return emitData;
    }
}
